import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React, { useState } from "react";
import { Persona } from "../../models";
import { LanguageCode } from "../../models";
import { PersonaTypeSelect } from "./PersonaTypeSelect";
import { PersonaPageConfigFormDialog } from "./PersonaPageConfigFormDialog";
import { PersonaPromptConfigFormDialog } from "./PersonaPromptConfigFormDialog";
import { PersonaViewDialog } from "./PersonaViewDialog";
import { PersonaCompanySelect } from "./PersonaCompanySelect";
import { useSnackbar } from "notistack";


interface PersonaFormProps {
  persona: Persona;
  languageCodes: LanguageCode[];
  errors?: Record<keyof Persona, string>;
  onPersonaChange: (persona: Persona) => void;
  updateTags: (newValue: any[]) => void;
  tags: any;
}

export const PersonaForm: React.FC<PersonaFormProps> = ({ persona, languageCodes, errors, onPersonaChange, updateTags, tags }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pageConfigDialogOpen, setPageConfigDialogOpen] = useState(false);
  const [promptConfigDialogOpen, setPromptConfigDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  function handlePersonaChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) {
    const field = event.target.name;
    onPersonaChange({ ...persona, [field]: event.target.value });
  }

  function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const field = event.target.name;
    onPersonaChange({ ...persona, [field]: checked });
  }

  async function handleCopyUuid() {
    try {
      await navigator.clipboard.writeText(persona.uuid);
      enqueueSnackbar("Persona UUID copied", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to copy persona UUID", { variant: "error" });
    }
  }

  // @ts-ignore
    const created = new Date(persona.createdAt)

  const tagOptions = tags.map((option: any) => {
    const firstLetter = option.name[0].toUpperCase();
    if (option.important) {
      return {
        firstLetter: " IMPORTANT",
        ...option
      }
    } else {
      return {
        firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
        ...option,
      };
    }
  });

  // @ts-ignore
  const defaultTags = persona.tagLinks.map((option : any) => {
    return tagOptions.filter((obj: any) => {
      return obj.id === parseInt(option.tagRef.id);
    })[0];
  });

  return (
    <>
      <div className="settings">
        <TextField error={!!errors?.name} helperText={errors?.name} className="spacer" variant="outlined" label="Name" name="name" value={persona.name} onChange={handlePersonaChange} />
        <TextField error={!!errors?.profileName} helperText={errors?.profileName} className="spacer" variant="outlined" label="Profile Name" name="profileName" value={persona.profileName} onChange={handlePersonaChange} />
        <TextField error={!!errors?.profilePictureUrl} helperText={errors?.profilePictureUrl} className="spacer" variant="outlined" label="Profile Picture URL" name="profilePictureUrl" value={persona.profilePictureUrl} onChange={handlePersonaChange} />

        <FormControl className="spacer" variant="outlined">
          <InputLabel>Type</InputLabel>
          <PersonaTypeSelect value={persona.type} onChange={handlePersonaChange} />
        </FormControl>

        <FormControl className="spacer" variant="outlined">
          <InputLabel>Language Code</InputLabel>
          <Select
            value={persona.languageCodeId}
            onChange={handlePersonaChange}
            label="Language Code"
            name="languageCodeId"
          >
            {languageCodes.map((languageCode) => (
              <MenuItem value={languageCode.id} key={languageCode.id}>{languageCode.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <PersonaCompanySelect
          className="spacer"
          value={persona.companyId}
          onChange={(companyId) => onPersonaChange({ ...persona, companyId })}
        />

        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={persona.masterPersona}
              onChange={handleSwitchChange}
              name="masterPersona"
              color="primary"
            />
          }
          label="Master Persona"
        />

        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={persona.processed}
              onChange={handleSwitchChange}
              name="processed"
              color="primary"
            />
          }
          label="Processed"
        />

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Button variant="contained" color="primary" onClick={() => setPageConfigDialogOpen(true)}>Page Config</Button>
          <Button variant="contained" color="primary" onClick={() => setPromptConfigDialogOpen(true)}>Prompt Config</Button>
          <Button variant="contained" color="primary" onClick={() => setViewDialogOpen(true)}>View</Button>
          <Autocomplete
              multiple
              defaultValue={defaultTags}
              renderInput={(params) => (
                  <TextField
                      style={{ marginBottom: "10px" }}
                      variant="outlined"
                      {...params}
                      label="Tags"
                  />
              )}
              options={tagOptions.sort(
                  (a: any, b:any) => -b.firstLetter.localeCompare(a.firstLetter)
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              groupBy={(option) => option.firstLetter}
              getOptionLabel={(option) => option.name}
              onChange={(e, newValue) => updateTags(newValue)}
          />
        </div>
          <div style={{marginTop: "5px"}}>Created : {created.getDate()} / {created.getMonth()+1} / {created.getFullYear()}</div>
          <Button
              variant="contained"
              color="primary"
              startIcon={<ContentCopyIcon />}
              disabled={!persona.uuid}
              onClick={handleCopyUuid}
          >
            Copy UUID
          </Button>
      </div>

      <PersonaPageConfigFormDialog
        open={pageConfigDialogOpen}
        onOpenChange={setPageConfigDialogOpen}
        pageConfig={persona.pageConfig}
        onPageConfigChange={(pageConfig) => onPersonaChange({ ...persona, pageConfig })}
      />

      <PersonaPromptConfigFormDialog
        open={promptConfigDialogOpen}
        onOpenChange={setPromptConfigDialogOpen}
        promptConfig={persona.promptConfig}
        onPromptConfigChange={(promptConfig) => onPersonaChange({ ...persona, promptConfig })}
      />

      <PersonaViewDialog personaUuid={persona?.uuid ?? ""} open={viewDialogOpen} onOpenChange={setViewDialogOpen} personaType={persona.type} />
    </>
  );
};
